"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { CurrencyBadge } from "@/components/currency";

export type SourceSelectItem = {
  value: string;
  /** Asset code shown as the badge + bold prefix (flag for fiat, token mark for stablecoins). */
  code?: string;
  label: string;
};

/**
 * Same visual language as CurrencySelect (badge + code + label + chevron in
 * a custom dropdown) — the funding-source picker used by the Payouts "you
 * send" step and the Convert tab, so both read as the same control.
 */
export default function SourceSelect({
  items,
  value,
  onChange,
}: {
  items: SourceSelectItem[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = items.find((s) => s.value === value);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button type="button" className="field flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        {selected?.code && <CurrencyBadge code={selected.code} />}
        <span className="font-normal">{selected?.code ?? "Select…"}</span>
        <span className="min-w-0 flex-1 truncate text-[13px]" style={{ color: "var(--c-ink-soft)" }}>
          {selected?.label}
        </span>
        <ChevronsUpDown size={14} style={{ color: "var(--c-ink-soft)" }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-[6px] border py-1"
          style={{ background: "var(--c-white)", borderColor: "var(--c-hairline)" }}
        >
          {items.map((s) => (
            <button
              key={s.value}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] transition-colors duration-150 hover:bg-[var(--c-cream)]"
              style={{ fontWeight: s.value === value ? 500 : 300 }}
              onClick={() => {
                onChange(s.value);
                setOpen(false);
              }}
            >
              {s.code && <CurrencyBadge code={s.code} />}
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              {s.value === value && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
